import { FixedSection } from '@/sections/types/sections.types';
import { BUILDER_DAY_OPTIONS, BUILDER_EVENT_TYPES as FIXED, BUILDER_LESSON_STATUS, BUILDER_RULES, COURSE_API, DAYS_OF_WEEK, BUILDER_SYNC_STATUS, BUILDER_STATUS } from '../values/courses.values';
import { RequestProvider } from '@/shared/providers/request.provider';
import { IRunSubjectBuilderService } from '../interfaces/subject-builder.interfaces';
import { SECTION_TYPES } from '@/content/values/sections';
import { CourseTemplateService } from '@/course-templates/services/course-templates.service';
import { FILESCACHE_KEYS } from '@/shared/plugins/files-cache/enums/files-cache.enum';
import Vue from 'vue';
import { BuilderData, BuilderLesson, BuilderRule, BuilderSchoolDaySlot, Parent, Structure } from '@/app/private/subjects/models/builder.model';
import { sectionToBuilderLesson } from '@/app/private/subjects/mappers/builder.mapper';
import { SchoolCalendarService } from '@/tools/services/school-calendar.service';
import { ScheduleService } from '@/tools/services/schedule.service';
import Moment from 'moment';
import { ContentSchedule } from '@/tools/models/class-builder.models';
import { AUTOMATIC_FOLDER_BUILDER } from '@/course-templates/values/course-templates.values';
import { BuilderServiceQueue } from './queue-builder.service';
import { APP_API } from '@/core/values/app.values';
import log from 'js-logger';
import { CyclicList } from './cyclic-list';
import { ABWorkingdays, ScheduleTypeEnum, ABShortWorkingDays } from '@/tools/enums/schedule-type.enum';

const DEFAULT_TYPES = [FIXED.CONTENT, FIXED.FILLER];
class BuilderServiceClass {

  async getSubjectDataBuilderFromServer(builderId, classId) {
    //Call download service
    const req = COURSE_API.DOWNLOAD_SUBJECT_BUILDER
      .replace('{builderId}', String(builderId));
    const responseData = await RequestProvider.get(req);

    //Build fixedLessonsMap
    const fixedLessons = new Map(responseData.fixedDateLessons.map((data) => [data, data]));

    //Build Detail subject and builer Data
    let builderData = this.buildBuilderData(responseData, classId, builderId);

    //Build Lessons by rootType(CONTENT, FILLER, FUN or ADDITIONAL)
    await this.buildLessonsByRootType(builderData, responseData.subject, fixedLessons);

    //Build calendar slot from server calendar data 
    await this.setCalendarSlots(builderData, 
      builderData.specialDays,
      builderData.workingDays, 
      builderData.schoolDay, 
      (responseData.schedule?.length > 0) ? builderData.contentId : null);//If the schedule has information, then send contentid

    //Update slots data from lessons
    this.updateSlotsByLessons(builderData.allLessonsMap, builderData.schoolDaysSlotsMap);
  
    //Build snaptshot structure
    const structure = await this.loadStructure(responseData, builderData);

    //Save on indexDB
    await Vue.prototype.$BuilderCache.cacheFile(`snaptshot_${builderId}`, structure.toSnapshot(), FILESCACHE_KEYS.BUILDER_DATA);
    await Vue.prototype.$BuilderCache.cacheFile(`builder_${builderData.builderId}`, builderData, FILESCACHE_KEYS.BUILDER_DATA);
    
    return builderData;
  }

  async setCalendarSlots(builderData, holidayPlan, workingDays, schoolDay , quickContentId?: number ) {  
    builderData.schoolDaysSlotsMap = new Map<string,BuilderSchoolDaySlot>();
    builderData.schoolDaysSlots = await this.buildSchoolDaySlots(schoolDay.dates, 
                                            workingDays, 
                                            holidayPlan, 
                                            builderData.scheduleType,
                                            builderData.scheduleShortDay,
                                            builderData.schedule, 
                                            quickContentId, 
                                            builderData.schoolDaysSlotsMap);
  }

  private buildBuilderData(responseData: any, classId: number, builderId: any) {
    const contentDetail = {
      id: responseData.subject.content.contentId,
      title: responseData.subject.content.contentTitle,
      img: responseData.subject.content.coverThumbnailSmallUrl,
      defaultDueDateForTasks: responseData.subject.content.defaultDueDateForTasks,
      defaultLastDateForTasks: responseData.subject.content.defaultLastDateForTasks,
      contentSummary: responseData.subject.content.contentSummary,
      categoryId: responseData.subject.content.category.id,
      imgHash: responseData.subject.content.coverImg,
      hasGrading: responseData.subject.content.hasGrading,
      actualGrade: responseData.subject.content.actualGrade,
      suggestedGrade: responseData.subject.content.suggestedGrade,
      suggestedLessonDuration: responseData.subject.content.suggestedLessonDuration,
      schoolDay: responseData.schoolDay.schoolDayId,
      schoolDayComplate: responseData.schoolDay.schoolDay,
      duration: responseData.subject.content.duration,
      components: responseData.subject.components,
      type: responseData.subject.type,
      classId: classId ? classId : null,
      fitFirstAvailable: responseData.fitFirstAvailable,
      lessonVariance: responseData.lessonVariance,
      componentsSelected: responseData.componentsSelected,
      status: responseData.subject.content.status,
      automaticFolderType: responseData.automaticFolderType,
    };

    let builderData: BuilderData = {
      workingDays: responseData.workingDays,
      schoolDay: responseData.schoolDay,
      specialDays: responseData.specialDays,
      scheduleType: responseData.scheduleType || ScheduleTypeEnum.WEEK,
      scheduleShortDay: responseData.scheduleShortDay,
      schedule: (responseData.schedule?.length > 0) ? this.buildMapSchedule(responseData.schedule) : null,
      subject: contentDetail,
      automaticFolderType: responseData.automaticFolderType,
      componentsSelected: responseData.componentsSelected,
      status: responseData.status,
      builderId: builderId,
      syncStatus: BUILDER_SYNC_STATUS.DONE,
      templateId: responseData.templateId,
      version: responseData.version,
      contentId: responseData.contentId,
      translateIds: false,
      initialType: responseData.initialType,
    };
    return builderData;
  }

  async buildLessonsByRootType(builderData, sectionsData, fixedLessons) {
    builderData.allLessonsMap = new Map<number, BuilderLesson>();
    builderData.allContentLessons = [];
    builderData.allFillerLessons = [];
    builderData.allFunLessons = [];
    builderData.additionalLessons = [];
    this.extractLessonFromDownloadData(sectionsData.contentSections?.sections, builderData, fixedLessons);
    this.extractLessonFromDownloadData(sectionsData.fillerSections?.sections, builderData, fixedLessons);
    this.extractLessonFromDownloadData(sectionsData.funSections?.sections, builderData, fixedLessons);
    this.extractLessonFromDownloadData(sectionsData.additionalSections?.sections, builderData, fixedLessons);
    
    //Sort lessons by initialFixedSequence to have the original order
    const compareLessons = (a, b) => a.fixedSequence - b.fixedSequence;
    builderData.allContentLessons = builderData.allContentLessons.sort(compareLessons);
    builderData.allFillerLessons  = builderData.allFillerLessons.sort(compareLessons);
    builderData.allFunLessons     = builderData.allFunLessons.sort(compareLessons);
  }

  async updateSlotsByLessons(allLessonsMap = new Map<number, BuilderLesson>(), schoolDaysSlotsMap = new Map<string, BuilderSchoolDaySlot>()) {
    allLessonsMap.forEach((lesson) => {
      let currentDate = lesson.startSchoolDate;

      while (currentDate <= lesson.endSchoolDate) {
        if (schoolDaysSlotsMap.has(currentDate)) {
          const slot = schoolDaysSlotsMap.get(currentDate);
          if (slot) {
            slot.lessons.push(lesson.id);
            slot.timeAvailable = 0;//TODO review for quick content
            if(lesson.status === BUILDER_LESSON_STATUS.FIXED)  {
              slot.status = BUILDER_LESSON_STATUS.FIXED;
            }
            this.addParentDataToLesson(lesson, slot);
            schoolDaysSlotsMap.set(currentDate, slot);
          }
        }
        currentDate = this.addDays(currentDate, 1);
      }
    });
  }

  private addParentDataToLesson(lesson: BuilderLesson, slot: BuilderSchoolDaySlot) {
    if (!lesson.parent.weeks.find(weekId => weekId === slot.weekId)) {
      lesson.parent.weeks.push(slot.weekId);
      lesson.parent.months.push(slot.monthId);
    }
  }

  private addDays(date: string, days: number): string {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + days);
    return newDate.toISOString().split('T')[0]; // Format YYYY-MM-DD
  }

  runSubjectBuilder(data: IRunSubjectBuilderService) {
    const req = COURSE_API.RUN_SUBJECT_BUILDER
      .replace('{templateId}', String(data.templateId));
    const { templateId: _, ...subjectData } = data;

    return RequestProvider.post(req, subjectData);
  }

  private loadStructure(responseData: any, builderData: BuilderData) {
    let lessons : Map<number, any> = new Map<number, any>();
    let contentStructure    = this.extractFolders(responseData.subject.contentSections, lessons);
    let fillerStructure     = this.extractFolders(responseData.subject.fillerSections, lessons);
    let funStructure        = this.extractFolders(responseData.subject.funSections, lessons);
    let additionalStructure = this.extractFolders(responseData.subject.additionalSections, lessons);
    this.printFolder(contentStructure.values().next().value);

    //Remove STRUCTURE nodes from snapshot CONTENT structure
    contentStructure = this.cleanStructureFolders(contentStructure);
    additionalStructure = this.cleanStructureFolders(additionalStructure);
    this.printFolder(contentStructure.values().next().value);

    //Copy original map data to not update 
    return new Structure(contentStructure, fillerStructure, funStructure, additionalStructure, lessons,
      responseData.subject.materials, responseData.subject.projects, responseData.subject.deckLibrary,
      this.cleanLessons(builderData.allContentLessons), this.cleanLessons(builderData.allFillerLessons), this.cleanLessons(builderData.allFunLessons),
      this.cleanLessons(builderData.additionalLessons), this.initializeLessonsMap(builderData.allLessonsMap));
  }

  private cleanStructureFolders(folderMap : Map<number, any>){
    const filteredStructure = new Map<number, any>();
    folderMap.forEach((node, key) => {
      if (node.fixedSection !== 'STRUCTURE') {
        node.sections = node.sections.filter(child => child.fixedSection !== 'STRUCTURE');
        filteredStructure.set(key, node);
      }
    });
    return filteredStructure;
  }
  private cleanLessons(lessons?: BuilderLesson[]): BuilderLesson[] | undefined {
    return lessons?.map(lesson => this.resetLessonSlotData({...lesson}));
  }

  private initializeLessonsMap(map?: Map<number, BuilderLesson>): Map<number, BuilderLesson> | null {
    const newMap = new Map(
      Array.from(map.entries()).map(([key, value]) => [key, this.resetLessonSlotData({ ...value })])
    );
    return newMap;
  }


  /**
   * Get template structure from indexDB or call backend service
   * @param templateId 
   * @returns 
   */
  async getTemplatesStructureById(templateId: number) {
    let data = null;

    if(templateId > 0) {
      data = await Vue.prototype.$BuilderCache.getFile(`template_${templateId}`, FILESCACHE_KEYS.BUILDER_TEMPLATES);
    }

    if(!data || !data.file) {
      data = await this.getTemplateData(data, templateId);
    } else {
      data = data.file;
    }

    return data;
  }

  /**
   * Get template structure from indexDB or call backend service
   * @param templateId 
   * @returns 
   */
  async updateTemplatesStructureFromServer(templateId: number) {
    let data = null;
    //If the server is running then we going to download template data again to update indexDB
    const isConnected = await BuilderServiceQueue.getServerStatus(APP_API.HEALTH_API);
    if (isConnected) {
      return await this.getTemplateData(data, templateId);
    } else {
      return await this.getTemplatesStructureById(templateId);
    }
  }

  private async getTemplateData(data: any, templateId: number) {
    data = await CourseTemplateService.getCourseTemplatesByIds([templateId]);

    //Extract the folders structure by fixed sections
    let lessons = new Map<number, any>();
    data.contentStructure = this.extractFolders(data.templates[0].contentSections, lessons, FIXED.CONTENT);
    data.fillerStructure = this.extractFolders(data.templates[0].fillerSections, lessons, FIXED.FILLER);
    data.funStructure = this.extractFolders(data.templates[0].funSections, lessons, FIXED.FUN);
    data.additionalStructure = this.extractFolders(data.templates[0].additionalSections, lessons, FIXED.ADDITIONAL);
    data.lessons = lessons;
    //Save on Local DB
    await Vue.prototype.$BuilderCache.cacheFile(`template_${templateId}`, data, FILESCACHE_KEYS.BUILDER_TEMPLATES);
    return data;
  }

  getSubjectCalendarFromBuilder(contentId: number) {
    const req = COURSE_API.GET_SUBJECT_CALENDAR.replace('{contentId}', String(contentId));
    return RequestProvider.get(req);
  }

    /**
   * Get organization calendars data from indexDB or call backend service
   * @param entityId 
   * @returns 
   */
    async getSchoolCalendarListFromServer(organizationId: number) {
      let startDate : string = Moment().format('YYYY-MM-DD');
      let endDate   : string = Moment().add(3, 'years').format('YYYY-MM-DD');
      let schoolDates = await SchoolCalendarService.getSchoolCalendarList('ORG', organizationId, endDate, startDate);
      if(schoolDates.length > 0)
        await Vue.prototype.$BuilderCache.cacheFile(`org_calendars_${organizationId}`, schoolDates, FILESCACHE_KEYS.BUILDER_DATA);
    return schoolDates;
  }

  /**
   * Get organization calendars data from indexDB or call backend service
   * @param entityId 
   * @returns 
   */
  async updateSchoolCalendarListFromServer(organizationId: number) {
    //If the server is running then we going to download data again to update indexDB
    const isConnected = await BuilderServiceQueue.getServerStatus(APP_API.HEALTH_API);
    if (isConnected) {
      return await this.getSchoolCalendarListFromServer(organizationId);
    } else {
      return await this.getSchoolCalendarList(organizationId);
    }
  }

  /**
   * Get organization calendars data from indexDB or call backend service
   * @param entityId 
   * @returns 
   */
  async getSchoolCalendarList(organizationId: number) {
    const data = await Vue.prototype.$BuilderCache.getFile(`org_calendars_${organizationId}`, FILESCACHE_KEYS.BUILDER_DATA);
    let schoolDates;
    if(!data || !data.file) {
      let startDate : string = Moment().format('YYYY-MM-DD');
      let endDate   : string = Moment().add(3, 'years').format('YYYY-MM-DD');
      schoolDates = await SchoolCalendarService.getSchoolCalendarList('ORG', organizationId, endDate, startDate);
      if(schoolDates.length > 0)
        await Vue.prototype.$BuilderCache.cacheFile(`org_calendars_${organizationId}`, schoolDates, FILESCACHE_KEYS.BUILDER_DATA);
    } else {
      schoolDates = data.file;
    }

    return schoolDates;
  }

  /**
   * Get a the class calendar data from indexDB or call backend service
   * @param entityId 
   * @returns 
   */
  async getClassCalendarList(classId: number) {
    const data = await Vue.prototype.$BuilderCache.getFile(`class_calendars_${classId}`, FILESCACHE_KEYS.BUILDER_DATA);
    let schoolDates;
    if(!data || !data.file) {
      schoolDates = await SchoolCalendarService.getSchoolCalendarList('CLASS', classId, '', '');
      await Vue.prototype.$BuilderCache.cacheFile(`class_calendars_${classId}`, schoolDates, FILESCACHE_KEYS.BUILDER_DATA);
    } else {
      schoolDates = data.file;
    }

    return schoolDates;
  }

  /**
   * Get a the class calendar data from indexDB or call backend service
   * @param entityId 
   * @returns 
   */
  async updateClassCalendarListFromServer(classId: number) {
    //If the server is running then we going to download data again to update indexDB
    const isConnected = await BuilderServiceQueue.getServerStatus(APP_API.HEALTH_API);
    if (isConnected) {
      return await this.getClassCalendarListFromServer(classId);
    } else {
      return await this.getClassCalendarList(classId);
    }
  }

  /**
   * Get a the class calendar data from indexDB or call backend service
   * @param entityId 
   * @returns 
   */
  async getClassCalendarListFromServer(classId: number) {
      let schoolDates = await SchoolCalendarService.getSchoolCalendarList('CLASS', classId, '', '');
      if(schoolDates.length > 0)
        await Vue.prototype.$BuilderCache.cacheFile(`class_calendars_${classId}`, schoolDates, FILESCACHE_KEYS.BUILDER_DATA);

    return schoolDates;
  }


  /**
   * Get subject schedule data from indexDB or call backend service and build a map for each week day
   * @param classId 
   * @param quickContentId 
   * @returns 
   */
  async getSubjectSchedule(classId: number, quickContentId: number) {
    let data = await ScheduleService.getScheduleByContentId(classId, quickContentId);
    /*const stored =  await Vue.prototype.$BuilderCache.getFile(`schedule_${quickContentId}`, FILESCACHE_KEYS.BUILDER_DATA);
    if(!stored || !stored.file) {
      data = await ScheduleService.getScheduleByContentId(classId, quickContentId);
      await Vue.prototype.$BuilderCache.cacheFile(`schedule_${quickContentId}`, data, FILESCACHE_KEYS.BUILDER_DATA);
    } else{
      data = stored.file;
    }*/

    return this.buildMapSchedule(data);
  }

  async getScheduleType(classId: number){
    return await ScheduleService.getScheduleType(classId);
  }

  private buildMapSchedule(data: any) {
    let schedule = new Map<string, ContentSchedule[]>();
    data.forEach(entry => {
      if (!schedule.has(entry.workingDay))
        schedule.set(entry.workingDay, []);
      schedule.get(entry.workingDay)!.push(entry);
    });
    return schedule;
  }

  /**
   * Filter lessons by components selected
   * @param allLessons 
   * @param components 
   */
  async filterLessonsByComponentSelectedMap( allLessons: Map<number, BuilderLesson>,  additionalLessons: BuilderLesson[], components: any[]) {
    const lessonsFiltered : Map<number, BuilderLesson> = new Map<number, BuilderLesson>();
    allLessons.forEach((lesson, key) => {
      let ignoreLesson = true;
      lesson.objectsComponent?.forEach((component) => {
        if (components.some(item => item.id === component)) {
          ignoreLesson = false;
        }
      });
      if (ignoreLesson) {
        if(lesson.rootType === FIXED.CONTENT){  //Just move CONTENT lessons to additional, all others stay in it's root type)
          additionalLessons.push(lesson);
        }
      } else {
        lessonsFiltered.set(lesson.id, lesson);
      }
    });
    return lessonsFiltered;
  }

  /**
   * Get lessons by root tyep (CONTENT, FILLER, FUN) and add each lesson to allLessons too
   * @param data 
   * @param rootFixedSection 
   * @param allLessons 
   * @param allLessonsByRoot 
   */
  async getBuilderAllLessonsFromCache(data: any, rootFixedSection: string, allLessonsByRoot: BuilderLesson[], allLessonsMap: Map<number, BuilderLesson>, fixedSequenceAllLessons: number = 0) {
    for (const template of data.templates) {
      fixedSequenceAllLessons = await this.extractLessonFromSectionsData(template, rootFixedSection, allLessonsByRoot, allLessonsMap, fixedSequenceAllLessons);
    }
    return fixedSequenceAllLessons;
  }

  async extractLessonFromSectionsData(template: any, rootFixedSection: string, allLessonsByRoot: BuilderLesson[], allLessonsMap: Map<number, BuilderLesson>, fixedSequenceAllLessons: number = 0) {
    let sections: any[];
    
    switch (rootFixedSection) {
      case FIXED.CONTENT:
        sections = template.contentSections?.sections;
        break;
      case FIXED.FILLER:
        sections = template.fillerSections?.sections;
        break;
      case FIXED.FUN:
        sections = template.funSections?.sections;
        break;
    }

    if (sections) {
      fixedSequenceAllLessons = this.extractSections(sections, allLessonsByRoot, rootFixedSection, template.content.suggestedLessonDuration, allLessonsMap, fixedSequenceAllLessons);
    }
    return fixedSequenceAllLessons;
  }

  /**
   * Maps the calendar data to the available Calendar Slots (new model on builder.model.ts)
   * @param dates 
   * @param workingDays 
   * @param holidayPlan 
   * @returns 
   */
  buildSchoolDaySlots(dates: string[], 
                    workingDays: string[], 
                    holidayPlan: any[], 
                    scheduleType: string = ScheduleTypeEnum.WEEK,
                    scheduleShortDay: string,
                    schedule: Map<string, ContentSchedule[]>, 
                    quickContentId: number, 
                    schoolDaysSlotsMap: Map<string, BuilderSchoolDaySlot>) : Array<BuilderSchoolDaySlot>{
    const slots : Array<BuilderSchoolDaySlot> = [];
    const holidayPlanMap = this.buildHolidayMap(holidayPlan);
    let schoolDay = 1;
    const abDays = new CyclicList<ABWorkingdays>([ABWorkingdays.A, ABWorkingdays.B]);

    dates.forEach(dateString => {
      const date = new Date(dateString + 'T00:00:00');
      const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();
      let dayAB = abDays.next();
      if(dayOfWeek === scheduleShortDay){  // If the date(dayOfWeek) matches the class short day, use the short day template
        dayAB = ABShortWorkingDays[dayAB];
      }

      let types = DEFAULT_TYPES;
      let totalTime = null;
  
      let dayToCheck = scheduleType == ScheduleTypeEnum.AB ? dayAB : dayOfWeek;
      let available = this.isWorkingDay(date, workingDays) && this.hasSchedle(quickContentId, dayToCheck, schedule);
     
      if(available){
        const scheduleBlocks = schedule ? schedule.get(dayToCheck) : [];
        if(holidayPlanMap.has(dateString)){
          const holiday = holidayPlanMap.get(dateString);
          if(holiday.status === BUILDER_DAY_OPTIONS.FUN){
            totalTime = this.calculateAvailableTime(quickContentId, dateString, scheduleBlocks);
            types = [BUILDER_DAY_OPTIONS.FUN];
          } else if(holiday.status === BUILDER_DAY_OPTIONS.IGNORED || holiday.status === BUILDER_DAY_OPTIONS.NOTIFIED){
            totalTime = this.calculateAvailableTime(quickContentId, dateString, scheduleBlocks);
          } else if(holiday.status === BUILDER_DAY_OPTIONS.OBSERVED){
            available = false;
          }
        } else {
          totalTime = this.calculateAvailableTime(quickContentId, dateString, scheduleBlocks);
        }
      }
      const slot = new BuilderSchoolDaySlot(schoolDay++, dateString, date, dayOfWeek, dayAB, types, available ? totalTime : 0);
      slots.push(slot)
      schoolDaysSlotsMap.set(dateString, slot)
    })
    return slots;
  }

  private isWorkingDay(date: Date, workingDays: string []): boolean {
    const dayOfWeek = DAYS_OF_WEEK[date.getDay()];  //Get the day name according the day number
    return workingDays.includes(dayOfWeek); 
  }

  private hasSchedle(quickContentId: number, dayOfWeek: string, schedule : Map<string, ContentSchedule[]>, ){
    if(!quickContentId) //when no quickContentId, asume lesson x day so schedule is not required
      return true;
    if(!schedule)
      return false;
    return schedule.has(dayOfWeek);
  }
  /**
   * Calcuclates available time in a date for a content using que Class Schedule.
   * For each date, evaluate the Class Calendar configuration. 
   * If the content is the default content, check for exception dates with different content to determine whether to add to the available time. 
   * If the content is not the default, verify if this content is an exception for the date under review and should be added to the available time.
   * @param quickContentId 
   * @param date 
   * @param blocks 
   * @returns 
   */
  private calculateAvailableTime(quickContentId: number, date: string, blocks: ContentSchedule[] = []){
    let totalTime = 0;

    if(!quickContentId)  //When not a quick content, return null to asume day x lesson
      return null;

    blocks.forEach(b => {
      if(b.defaultContentId === quickContentId){
        //If the content is the default for the block and there is no exceptions on the date, take the total time
        if(!hasAnyExceptionForDate(b, date))
          totalTime += b.totalTime;
      } else if(hasExceptionForContentOnDate(b, quickContentId, date)){
        //If the content is not the default for the block, but there is an exception for it on the date, take the total time
        totalTime += b.totalTime;
      }
    });
    return totalTime;

    /**
     * Check if the block has any exceptions on the date specified in the parameter.
     */
    function hasAnyExceptionForDate(block: ContentSchedule, date: string): boolean {
      for (const exceptionId in block.exceptionDates) {
        if (block.exceptionDates[exceptionId].includes(date)) 
          return true;
      }
      return false; 
    }
  
    /**
     * Check if the block has an exception for the content and the date specified in the parameters.
     */
    function hasExceptionForContentOnDate(block: ContentSchedule, contentId: number, date: string): boolean {
      if (contentId in block.exceptionDates)
        return block.exceptionDates[contentId].includes(date);
      return false;
    }
  }

  /**
   * Creates a Holiday Map priorizing by holiday status
   * @param holidayPlan 
   * @returns 
   */
  private buildHolidayMap(holidayPlan: any[]) : Map<string, any>{
    const holidayPlanMap: Map<string, any> = new Map<string, any>();
    holidayPlan.forEach(hp => {
      if (hp.endDate === null || hp.endDate === hp.startDate) {
        this.prioritizeDay(hp.startDate, hp, holidayPlanMap)
      } else {
          for (let d = new Date(hp.startDate), end = new Date(hp.endDate); d <= end; d.setDate(d.getDate() + 1)) {
              const formattedDate = d.toISOString().split('T')[0];
              this.prioritizeDay(formattedDate, hp, holidayPlanMap)
          }
      }
    })
    // holidayPlanMap.forEach((value, key) => console.log(`Key: ${key}, Value: ${JSON.stringify(value)}`));
    return holidayPlanMap;
  }

  /**
   * Check the holiday to determine which condition should be applied.
   * OBSERVED takes precedence over FUN & IGNORED
   * FUN takes precedence over IGNORED 
   * @param date 
   * @param holiday 
   * @param current 
   * @returns 
   */
  private prioritizeDay(date: string, holiday: any, current: Map<string, any>){
    if(current.has(date)){
      const currentType = holiday.status;
      if(currentType === BUILDER_DAY_OPTIONS.OBSERVED)           //If current is OBSERVED, has the priority
        return;
      else if(holiday.status === BUILDER_DAY_OPTIONS.OBSERVED)   //If new is OBSERVED, get priority   
        current.set(date, holiday)
      else if(holiday.status === BUILDER_DAY_OPTIONS.FUN)        //If new is FUN, override (current is IGNORED)
        current.set(date, holiday)
    } else {
      current.set(date, holiday)
    }
  }
  
  /**
   * Suffle lessons by school day slot
   * @param slots 
   * @param allLessons 
   * @param variance 
   * @param automaticFolderType 
   */
  async shuffleLessonsBySlotsByMap(builder : BuilderData) {
    const slots = builder.schoolDaysSlotsMap;
    const allLessons = builder.allLessonsMap
    //slots.forEach(slot =>  console.log('Slots  :::::::::: ==>', slot.schoolDay, slot.dayOfWeek, slot.date.toISOString().split('T')[0], slot.weekId, slot.monthId, slot.timeAvailable, JSON.stringify(slot.allowedTypes), slot.lessons))
    // allLessons.forEach(lesson =>  console.log('LESSON :::::: ==>', lesson.name, lesson.id, lesson.status, lesson.startDay, lesson.startSchoolDate, lesson.fixedSequence, lesson.parent.weeks));
    const today = new Date();
    slots.forEach((slot, key) => {
        if(slot.date.getTime() > today.getTime() &&
           slot.status != BUILDER_LESSON_STATUS.FIXED && 
           (slot.timeAvailable === null || slot.timeAvailable > 0)) {
          let lesson: BuilderLesson;
          do {
            lesson = null;
            const lessonFound = this.findFirstLessonPendingByRootTypeMap(slot.allowedTypes, allLessons);
            if(lessonFound) {
              lesson = lessonFound;
            } else{ 
              //When the lesson is not found in allLessons, it could be cause other Lessons were discard (specially if the Subject was UPLOADED),
              //So we try to find a FUN or ADDITIONAL or FILLER lesson, allways validating the components selected
              //Note: Lessons initially discarded by components could be added at the end when those components are selected again

              if (slot.allowedTypes.includes(FIXED.FUN)) {  //When no lesson found and slot type is FUN, check if there is a fun lesson available
                lesson = this.findNextFunLesson(builder);
              } else if (slot.allowedTypes.includes(FIXED.CONTENT)) { 
                //When the slots if for CONTENT, first check if there is an additional lesson available or a Filler
                if(builder.additionalLessons?.length > 0)
                  lesson = builder.additionalLessons.find(additionalLesson =>       //Find the lesson checking the components match to selected
                              additionalLesson.objectsComponent?.some(objectComponent => builder.componentsSelected.some(component => component.id === objectComponent))  
                            );
                
                if (lesson) {
                  builder.additionalLessons = builder.additionalLessons.filter(l => l.id !== lesson.id);  //Remove from additional
                } else {  //Try to find a Lesson from fillers
                  lesson = this.findNextFillerLesson(builder);
                }
              } 
              if(lesson) {  //A lesson was found on FUN or ADDITIONAL or FILLER
                lesson.movedByBuilder = true;
                allLessons.set(lesson.id, lesson);
              }
            }

            if(lesson) {
              this.assignDateToLesson(slot, lesson, builder.subject?.lessonVariance, builder.subject?.fitFirstAvailable);
            }
          } while (lesson != null && slot.timeAvailable > 0)
        }
      });

      this.sortLessonsBySchoolDay(allLessons);
     // allLessons.forEach(lesson =>  console.log('Lesson ****************** ==>', lesson.name, lesson.id, lesson.status, lesson.startDay, lesson.startSchoolDate));
  }

  /**
   * Find firts lessons pending by root type(content, filler, fun)
   * @param rootType 
   * @param allLessons 
   * @returns 
   */
  findFirstLessonPendingByRootTypeMap(rootTypes: string[], allLessons: Map<number, BuilderLesson>) : BuilderLesson {
    return Array.from(allLessons.values()).find(
      (lesson) => rootTypes.includes(lesson.rootType) && lesson.status === BUILDER_LESSON_STATUS.PENDING
    );
  }

  /**
   * Assign date to lesson by slot time
   * @param slot 
   * @param lesson 
   * @param variance 
   * @returns 
   */
  assignDateToLesson(slot: BuilderSchoolDaySlot, lesson: BuilderLesson, variance: number, fitFirstAvailable: boolean) {
    const assignLessonToSlot = () => {
      if (!lesson.startDay) {
        lesson.startDay = slot.schoolDay;
        lesson.startSchoolDate = slot.dateString;
      }
      lesson.endDay = slot.schoolDay;
      lesson.endSchoolDate = slot.dateString;
      this.addParentDataToLesson(lesson, slot);
      slot.lessons.push(lesson.id);
    };

    //The lesson has no explicit duration or the slot has not duration or the lesson should fit in the first slot 
    if(lesson.pendingTime == null || lesson.duration == null || slot.timeAvailable == null || fitFirstAvailable) {
      assignLessonToSlot();
      lesson.pendingTime = 0;
      lesson.status = BUILDER_LESSON_STATUS.ASSIGNED;
      slot.timeAvailable = 0;
      return;
    } 
    
    // The lesson is shorter than the slot time available (or the lesson + variance)
    if (lesson.pendingTime <= slot.timeAvailable || lesson.pendingTime <= (Number(slot.timeAvailable) + Number(variance))) {
      assignLessonToSlot();
      lesson.status = BUILDER_LESSON_STATUS.ASSIGNED;
      slot.timeAvailable = (lesson.rootType === FIXED.FUN) ? 0 : (Number(slot.timeAvailable ) - Number(lesson.pendingTime));
      lesson.pendingTime = 0;
      return;
    }
    // If lesson is longer than slot available time and slot has lessons, just clean the slot time (Do not assign multiple lessons on the same slot)
    if (lesson.pendingTime > slot.timeAvailable && slot.lessons?.length > 0) {
      slot.unusedTime = slot.timeAvailable;
      slot.timeAvailable = 0;
      return;
    }
  
    // If lesson is longer than slot time and slot has no lessons, assign the lesson (split the lesson)
    if (lesson.pendingTime > slot.timeAvailable && slot.lessons?.length === 0) {
      assignLessonToSlot();
      lesson.pendingTime = Number(lesson.pendingTime) - Number(slot.timeAvailable);
      slot.timeAvailable = 0;
      return;
    }
  }

  /**
   * Move lesson to additional content
   * @param builder 
   */
  async moveLessonToAdditionalContent(builder: BuilderData) {
    const additionalLessons: Map<number, BuilderLesson> = new Map(
      (builder.additionalLessons ?? []).map((lesson) => [lesson.id, lesson])
    );

    const lessonsFiltered: Map<number, BuilderLesson> = new Map();
    
    builder.allLessonsMap.forEach((lesson, id) => {
      if (lesson.startDay && lesson.startDay > 0) {
        lessonsFiltered.set(id, lesson);
      } else {
        this.resetLessonSlotData(lesson);
        if (lesson.rootType === FIXED.CONTENT && !additionalLessons.has(id)) { //Just move CONTENT lessons to additional, all others stay in it's root type)
          additionalLessons.set(id, lesson); 
        }
      }
    });
    
    builder.additionalLessons = Array.from(additionalLessons.values());
    builder.allLessonsMap = lessonsFiltered;
  }

  /**
   * Remove slot data time to lesson
   * @param allLessons 
   */
  async removeSlotDataToLessons(allLessonsMap: Map<number, BuilderLesson>) {
      allLessonsMap.forEach((lesson) => {
        if (lesson.status !== BUILDER_LESSON_STATUS.FIXED) {
          this.resetLessonSlotData(lesson);
        }
      });
  }

  /**
   * Reset date slot data to lesson
   * @param lesson 
   */
  private resetLessonSlotData(lesson: BuilderLesson): BuilderLesson {
    lesson.startDay = null;
    lesson.endDay = null;
    lesson.pendingTime = lesson.duration;
    lesson.status = BUILDER_LESSON_STATUS.PENDING;
    lesson.startSchoolDate = null;
    lesson.endSchoolDate = null;
    lesson.parent = new Parent(lesson.parent.parentId);
    lesson.movedByBuilder = false

    return lesson;
  }

/**
 * Reset the slots related to a Lesson, filtering by the lesson dates.
 * If there is more than one lesson in the slot, validate if the lesson is really in the slot (slot.lessons)
 * @param lesson 
 * @param builder 
 */
  private releaseSlotsByLesson(lesson: BuilderLesson, builder: BuilderData){
    log.debug(`resetSlotsByLesson (${lesson.id}) ${lesson.name}  ${lesson.startSchoolDate} to ${lesson.endSchoolDate}`);
    let date = new Date(lesson.startSchoolDate);
    let end = new Date(lesson.endSchoolDate);
    while (date <= end) {
      const dateToReset = date.toISOString().split('T')[0];
      const slot = builder.schoolDaysSlotsMap.get(dateToReset);
      log.debug(`resetSlotsByLesson dateToReset ${dateToReset}, lessons: ${slot?.lessons}  status:${slot?.status}`);
      if (slot && slot.lessons?.includes(lesson.id)) { // Check if the lesson is in the slot
        this.resetSlotData(slot);
      }
      date.setDate(date.getDate() + 1); // Move to the next day
    }
  }

  /**
   * Reset slot data time and lessons
   * @param slot 
   */
  private resetSlotData(slot: BuilderSchoolDaySlot) {
    slot.timeAvailable = slot.totalTime;
    slot.lessons = [];
    slot.status = undefined;
  }

  /**
   * Apply rule by type
   * @param rule 
   * @param allLessons 
   * @param slots 
   */
  async applyRules(rule: any, builder: BuilderData, isQuickSubject: boolean = false) {
    switch (rule.type) {
      case BUILDER_RULES.SWAP_LESSONS:
        this.ruleSwap(rule, builder);
        break;
      case BUILDER_RULES.MOVE_LESSON_WHEREVER:
        this.ruleWherever(rule, builder);
        break;
      case BUILDER_RULES.PLACE_LABELS_ON_WORKING_DAY:
        this.rulePlaceLabel(rule, builder);
        break;
    }
  }

  /**
   * Swap between 2 lessons
   * @param rule 
   * @param allLessons 
   * @param slots 
   */
  ruleSwap(rule: any, builder: BuilderData) {
    let target = builder.allLessonsMap.get(rule.lessonTargetId);
    let source = builder.allLessonsMap.get(rule.lessonSourceId);
    const resetFrom = (source.startDay < target.startDay) ? source.startSchoolDate : target.startSchoolDate;
    const _source = {...source}
    const _target = {...target}
    
    this.resetSlotDataFromSlotDay(resetFrom, builder.schoolDaysSlotsMap);
    this.resetLessonDataFromSlotDay(resetFrom, builder.allLessonsMap);

    //TODO
    /*
      - Pending review when a lesson that is going to move has a long time, and that time exceeds a lesson that was fixed later, then in this case we must do the following
       1. Assign time to the lesson that is moving and if in the range of this time there is a lesson in one of the slots to be assigned, then shuffle that lesson and so on until there is no longer that time overrun.
    */

    source.fixedSequence  = _target.fixedSequence;
    target.fixedSequence  = _source.fixedSequence;
    //Release the lesson and the slots to do de swap
    this.resetLessonSlotData(source); 
    this.resetLessonSlotData(target);
    this.releaseSlotsByLesson(_source, builder); 
    this.releaseSlotsByLesson(_target, builder);
    let recalculateOverlappingOn = [];
    if(_source.status === BUILDER_LESSON_STATUS.FIXED) {  //If the source is fixed then we need to fix the target to the source date
      recalculateOverlappingOn.push(_source.startSchoolDate); 
      this.shuffleLessonByStartDate(_source.startSchoolDate, target, builder)
      target.status = BUILDER_LESSON_STATUS.FIXED;
    } 
    if(_target.status === BUILDER_LESSON_STATUS.FIXED) { //If the target is fixed then we need to fix the source to the target date
      recalculateOverlappingOn.push(_target.startSchoolDate);
      this.shuffleLessonByStartDate(_target.startSchoolDate , source, builder);
      source.status = BUILDER_LESSON_STATUS.FIXED;
    }   

    if(source.parent.parentId === target.parent.parentId){
      target.movedByBuilder = false;
      source.movedByBuilder = false;
    } else {
      target.movedByBuilder = true;
      source.movedByBuilder = true;
    }   

    builder.allLessonsMap = this.sortMapByFixedSequence(builder.allLessonsMap);
    recalculateOverlappingOn.forEach(recalculateOverlappingOn => {
      this.shuffleOverlappingFixedLessons(recalculateOverlappingOn, builder);
    });
  }

  sortMapByFixedSequence(allLessons: Map<number, BuilderLesson>): Map<number, BuilderLesson> {
    return new Map(
      Array.from(allLessons.entries())
        .sort(([, a], [, b]) => a.fixedSequence - b.fixedSequence) 
    );
  }

  /**
   * Swap between 2 lessons
   * @param rule 
   * @param allLessons 
   * @param slots 
   */
  ruleWherever(rule: any, builder: BuilderData) {
    let source = builder.allLessonsMap.get(rule.lessonSourceId);
    let recalculateOverlappingOn = null;
    if(source.status === BUILDER_LESSON_STATUS.FIXED) { //When the lesson is fixed then we need to reset all it's fixed slots
      recalculateOverlappingOn = source.startSchoolDate;
      this.releaseSlotsByLesson(source, builder); 
      this.resetLessonSlotData(source);
    }

    this.resetSlotDataFromSlotDay(source.startSchoolDate, builder.schoolDaysSlotsMap);
    this.resetLessonDataFromSlotDay(source.startSchoolDate, builder.allLessonsMap);
  
    builder.allLessonsMap = this.sortMapByFixedSequence(builder.allLessonsMap);
    this.shuffleLessonByStartDate(rule.lessonDate, source, builder);

    source.status = BUILDER_LESSON_STATUS.FIXED;
    source.movedByBuilder = true;
    if(recalculateOverlappingOn)
      this.shuffleOverlappingFixedLessons(recalculateOverlappingOn, builder);  //Reset all overlapping fixed lessons on the previous date of the lesson moved 
    //TODO: Check if is requiered recalculate the fixedSequence of the moved lesson and the other lessons that are displaced
  }

  rulePlaceLabel(rule: any, builder: BuilderData) {
    /*let lessonsByTag = this.findLessonBySelectedTag(rule.label, builder.allLessons);
    let firtsSlot = builder.schoolDaysSlots.find(item => item.dayOfWeek === rule.workingDay);
    let slotByWorkingDay = builder.schoolDaysSlots.filter(item => item.dayOfWeek === rule.workingDay);
    this.resetLessonFixedDataFromSlotsDay(builder.allLessons, slotByWorkingDay);
    this.resetSlotDataFromSlotDay(firtsSlot.date.toString(), builder.schoolDaysSlots, firtsSlot);
    this.resetLessonDataFromSlotDay(firtsSlot.date.toString(), builder.allLessons, firtsSlot);
    this.shuffleLessonsBySlots(slotByWorkingDay, lessonsByTag, builder.subject.lessonVariance, builder.automaticFolderType);*/

    //TODO pending the process to move the lessons that run out of working days in the calendar to additional content
    //TODO pending to assign the attribute .movedByBuilder = true; to the moved lessons
  }

  findLessonBySelectedTag(tag: string, allLessons: BuilderLesson[]) {
    return allLessons.filter(item => item.tagsSection.some(item => item === tag));
  }

  /**
   * shufle lesson by start date
   * @param schoolDate 
   * @param lesson 
   * @param builder 
   * @returns 
   */ 
  shuffleLessonByStartDate(schoolDate: string, lesson: BuilderLesson, builder: BuilderData, addTime: boolean = true) {
    const slots = Array.from(builder.schoolDaysSlots.values())
    let slotIndex = -1;
    let slot:BuilderSchoolDaySlot = null;
    //Find the slot index for the given schoolDate
    for (let i = 0; i < slots.length; i++) {
      if (slots[i].dateString === schoolDate) {
        slotIndex = i;
        break;
      }
    }
    //Get the starting slot and assign the lesson (if allowed and has timeAvailable).  
    //If the lesson requires more time than the slot has available, it will continue to the next slot
    do{
      slot = slots[slotIndex++];
      let timeAvailable = slot.timeAvailable === null || slot.timeAvailable > 0; //When timeAvailable is null, it means that the slot is available for any lesson
      if (slot.allowedTypes.includes(lesson.rootType) && timeAvailable) { 
        this.assignDateToLesson(slot, lesson, 0, builder.subject?.fitFirstAvailable);
        slot.status = BUILDER_LESSON_STATUS.FIXED;                  //Slots is marked as FIXED cause this method is called for fixed lessons
        slot = lesson.pendingTime > 0 ? slots[slotIndex] : null;    //Stop if the lesson pending time is 0
      } else {
        slot = slotIndex < slots.length ? slots[slotIndex] : null;  //Go to next slot if the current one is not available or does not have the right type  
      }
    }while(slot);                                                   //Stop when no slot is found
  }
    
  /**
   * Add fun lesson to fun day
   * @param funDate 
   * @param builder 
   */
  async addFunLessonToFunDay(funDate: string, builder: BuilderData) {
    const slotFound = builder.schoolDaysSlotsMap.get(funDate);
    log.debug('Add FUN day at:', funDate);
    if (slotFound) {
      let resetFrom = this.findResetFrom(funDate, slotFound.lessons, builder.allLessonsMap);
      slotFound.allowedTypes = [FIXED.FUN];
      slotFound.lessons = [];
      this.resetSlotDataFromSlotDay(resetFrom, builder.schoolDaysSlotsMap);
      this.resetLessonDataFromSlotDay(resetFrom, builder.allLessonsMap);
      this.addFunLessonToLessonList(builder);
      slotFound.status = undefined;
      this.shuffleOverlappingFixedLessons(funDate, builder);  //Reset all overlapping fixed lessons on the fun date
    }
  }
  /**
   * Shuffle fixed lessons that overlap with the given date.
   * This method will reset the slots and lessons for all fixed lessons that overlap with the given date,
   * and then reshuffle them based on their start date.
   * @param date 
   * @param builder 
   */
  shuffleOverlappingFixedLessons(date: string, builder: BuilderData){
    //Find all fixed lessons that overlap with the given date
    let overlappingLessons = Array.from(builder.allLessonsMap.values())
        .filter(lesson => lesson.status === BUILDER_LESSON_STATUS.FIXED &&
                          lesson.startSchoolDate <= date &&
                          lesson.endSchoolDate >= date)
        .sort((a, b) => b.startSchoolDate.localeCompare(a.startSchoolDate));  //Sort by start date to reshuffle starting from the innermost

    //Maps the overlapping lessons by their start date for easier access and reset each lesson slot data and lesson data
    const overlappingMap = new Map<string, BuilderLesson>();
    overlappingLessons.forEach(lesson => {
      overlappingMap.set(lesson.startSchoolDate, lesson);
      this.releaseSlotsByLesson(lesson, builder);
      this.resetLessonSlotData(lesson);
    });

    //Reshuffle each overlapping lesson by its start date
    overlappingMap.forEach((lesson, key) => {
        this.shuffleLessonByStartDate(key, lesson, builder);
        lesson.status = BUILDER_LESSON_STATUS.FIXED;
        lesson.movedByBuilder = true;
    });
  }

  /**
   * Add standar date to slots
   * @param funDate 
   * @param builder 
   */
  async addStandardDate(standardDate: string, builder: BuilderData) {
    const slotFound = builder.schoolDaysSlotsMap.get(standardDate);
    log.debug('Add standar dat at:', standardDate);
    if (slotFound) {
      let resetFrom = standardDate;
      //Find the next slot that is not a FUN day
      const slotKeys = Array.from(builder.schoolDaysSlotsMap.keys());
      const currentIndex = slotKeys.indexOf(standardDate);

      for (let i = currentIndex + 1; i < slotKeys.length; i++) {
        const potentialSlot = builder.schoolDaysSlotsMap.get(slotKeys[i]);
        if (potentialSlot && potentialSlot.allowedTypes?.includes(FIXED.CONTENT)) {
          //If there is a next standard slot, find the resetFrom date based on its lessons
          resetFrom = this.findResetFrom(standardDate, potentialSlot.lessons, builder.allLessonsMap);
          break;
        }
      }
      
      slotFound.allowedTypes = [FIXED.CONTENT, FIXED.FILLER];
      slotFound.lessons = [];
      this.resetSlotDataFromSlotDay(resetFrom, builder.schoolDaysSlotsMap);
      this.resetLessonDataFromSlotDay(resetFrom, builder.allLessonsMap);
      this.shuffleOverlappingFixedLessons(standardDate, builder);  //Reset all overlapping fixed lessons on the standard date
    }
  }

  private findResetFrom(refDate : string, slotLessons: number[],  allLessonsMap?: Map<number, BuilderLesson>) : string{
    let resetFrom = refDate;
    if(slotLessons?.length > 0) {
      //If the slot has a lesson, then we need to find the lesson in the slot that has the earliest start date
      const lessonFound = slotLessons
            .map(lessonId => allLessonsMap.get(lessonId))
            .filter(lesson => lesson !== undefined)
            .reduce((first, current) => {
              return (!first || current.startSchoolDate < first.startSchoolDate) ? current : first;
            }, null);
      if (lessonFound) 
        resetFrom = resetFrom < lessonFound.startSchoolDate ? resetFrom : lessonFound.startSchoolDate;
    }
    log.debug('Slots will be reset from day:', resetFrom);
    return resetFrom;
  }

  /**
   * Add fun lesson to fun day on all lesson list
   * @param builder 
   */
  addFunLessonToLessonList(builder: BuilderData) {
    const countFUNDays = builder.schoolDaysSlots.filter(item => item.allowedTypes.includes(FIXED.FUN)).length;
    const countFUNLesson = Array.from(builder.allLessonsMap.values()).filter((lesson) => lesson.rootType === FIXED.FUN).length;
    if(countFUNDays > countFUNLesson) {
      this.findNextFunLesson(builder);
    }
  }
  /**
   * Find the next fun lesson that can be added to the lesson list
   * - Fun Lesson should match the selected components
   * - Fun Lesson should not be already included in the lesson map
   * @param builder 
   * @returns 
   */
  private findNextFunLesson(builder: BuilderData) {
    const funLessonFound = builder.allFunLessons
      .filter(item => item.objectsComponent?.some(item => builder.componentsSelected.some(component => component.id === item)))  //Matching components 
      .find(item1 => !builder.allLessonsMap.has(item1.id));  //Not in allLessonsMap currently

    if (funLessonFound) {
      builder.allLessonsMap.set(funLessonFound.id, funLessonFound);
    }
    return funLessonFound;
  }

  /**
   * Find the next filler lesson that can be added to the lesson list.  Used when the user changes the components selected on a DONE subject (Fillers excluded at the beginning)
   * - Filler Lesson should match the selected components
   * - Filler Lesson should not be already included in the lesson map
   * @param builder 
   * @returns 
   */
  private findNextFillerLesson(builder: BuilderData) {
    const fillerLessonFound = builder.allFillerLessons
      .filter(item => item.objectsComponent?.some(item => builder.componentsSelected.some(component => component.id === item)))  //Matching components 
      .find(item1 => !builder.allLessonsMap.has(item1.id));  //Not in allLessonsMap currently

    if (fillerLessonFound) {
      builder.allLessonsMap.set(fillerLessonFound.id, fillerLessonFound);
    }
    return fillerLessonFound;
  }

  /**
   * Remove lesson from additional lesson if it is found
   * @param additionalLessons 
   * @param lessonToRemove 
   * @returns 
   */
  removeLessonFromAdditionLessons(additionalLessons: BuilderLesson[], lessonToRemove: BuilderLesson) : BuilderLesson[] {
    return additionalLessons.filter(item => item.id !== lessonToRemove.id);
  }

  /**
   * Reset lesson data from slot day
   * @param slotDate 
   * @param lessons 
   * @param slotFound 
   */
  resetLessonDataFromSlotDay(slotDate: string, lessons: Map<number, BuilderLesson>) {
    // Iterate lessons until finding the first with startSchoolDate >= slotDate
    const date = Date.parse(slotDate);
    lessons.forEach((lesson) => {
      if (Date.parse(lesson.startSchoolDate) >= date && lesson.status !== BUILDER_LESSON_STATUS.FIXED) {
          this.resetLessonSlotData(lesson);
      }
    });
  }
  
  resetLessonFixedDataFromSlotsDay(lessons: BuilderLesson[], slots: BuilderSchoolDaySlot[]) {
    lessons
      .filter(item => item.status === BUILDER_LESSON_STATUS.FIXED )  
      .forEach((lesson) => {
        if (slots.some(item => item.lessons?.some(id => id === lesson.id))) {
          this.resetLessonSlotData(lesson);
        }
      });
  }

  /**
   * Reset slot data from slot date
   * @param slotDate 
   * @param slots 
   * @param slotFound 
   */
  resetSlotDataFromSlotDay(slotDate: string, slots: Map<string, BuilderSchoolDaySlot>) {
    const date = Date.parse(slotDate);
    slots.forEach((slot, key) => {
      if (Date.parse(key) >= date && slot.status !== BUILDER_LESSON_STATUS.FIXED) {
        this.resetSlotData(slot);
      }
    });
    
  }

  getTomorrowDate(): string {
    const today = new Date();
    today.setDate(today.getDate() + 1); 
    return today.toISOString().split('T')[0];
  }

  /**
   * Reset slot data time from minor lesson
   * @param source 
   * @param target 
   * @param slots 
   */
  private resetSlotDataFromFoundLesson(lesson: BuilderLesson, allLessons: Map<number, BuilderLesson>, slots: Map<string, BuilderSchoolDaySlot>, forceClean: boolean = false, forceCleanFixed: boolean = false) {
    let clean = false;
    slots
      .forEach((slot, key) => {
        if((slot.lessons?.includes(lesson.id) || (clean && slot.lessons?.length > 0)) 
              && (slot.status !== BUILDER_LESSON_STATUS.FIXED || forceClean)) {
          if(forceClean) {
            this.resetSlotDataWithMoreThanOneLesson(slot, lesson, allLessons);
            forceClean = false;
          } else {
            this.resetSlotData(slot);
          } 
          clean = true;
        } if((slot.lessons?.includes(lesson.id) && slot.status === BUILDER_LESSON_STATUS.FIXED && forceCleanFixed)) {
          this.resetSlotData(slot);
          clean = true;
        }
      });
  }

  private resetSlotDataWithMoreThanOneLesson(slot: BuilderSchoolDaySlot, lesson: BuilderLesson, allLessons: Map<number, BuilderLesson>) {
    slot.lessons = this.removeElementsFromArrayNumber(slot.lessons, lesson.id);
    const lessonsPendingTime = this.getPendingTimeFromLessons(slot.lessons, allLessons);
    slot.timeAvailable = slot.totalTime ? slot.totalTime - lessonsPendingTime : slot.totalTime;
    slot.status = undefined;
  }

  private sortLessonsBySchoolDay(allLessons: Map<number, BuilderLesson>): void {
    const sortedEntries = Array.from(allLessons.entries())
      .sort(([, lesson1], [, lesson2]) => lesson1.startDay - lesson2.startDay);
    
    allLessons.clear();
    sortedEntries.forEach(([key, value]) => allLessons.set(key, value));
  }

  private resetSlotDataFromList(slots: BuilderSchoolDaySlot[]) {
    slots
      .forEach((slot) => this.resetSlotData(slot));
  }

  /**
   * Reset lesson slot data from minor sequence 
   * @param source 
   * @param target 
   * @param allLessons 
   */
  private resetLessonDataFromMinorSequence(lessonMinor: BuilderLesson, allLessons: Map<number, BuilderLesson>, forceClean: boolean = false, fixedSequenceTarget = -1) {
    allLessons
      .forEach((lesson) => {
        if (lesson.startDay >= lessonMinor.startDay && lesson.fixedSequence > fixedSequenceTarget && (lesson.status !== BUILDER_LESSON_STATUS.FIXED || forceClean)) {
          this.resetLessonSlotData(lesson);
        }
      });
  }

  /**
   * Builds a Map of content folders, excluding lessons and it's sub folders
   * @param rootSection
   * @returns
   */
  extractFolders(content: any, lessons: Map<number, any>, rootType?: string) :  Map<number, any>{
    let folders = new Map<number, any>();
    this._extractFolders(content, folders, lessons, rootType)
    return folders;
  }

  /**
   * Recursive function to extract the Section Folders
   * @param section 
   * @param folders 
   * @returns 
   */
  private _extractFolders(section: any, folders:  Map<number, any>, lessons:  Map<number, any> , rootType?: string){
    if(section.type === 'SECTION'){
      const _rootType = rootType ? rootType : section.initialRootType;
      if(section.fixedSection === 'LESSON'){
        lessons.set(section.id, { ...section, rootType: _rootType });
      } else {
        let copy = { ...section, rootType: _rootType, sections: [], _sections: [] };
        folders.set(section.id, copy);  //Add the current node

        if(copy.parentId){
          const parent =  folders.get(copy.parentId);
          parent.sections.push(copy)     //Add the current node to the parent
          copy.path = [...parent.path, copy.id]  //Add the path to the current node
        } else {
          copy.path = [copy.id]
        }
        
        if(section.sections)
          section.sections.forEach(s => this._extractFolders(s, folders, lessons, rootType));
      }
    }
  }

  /**
   * Gets the structure folders of the builder.  If the structure doesn't exist, then get the original structure of the template.
   * Reads the structure saved for the builder/template each time to override changes made by the user on previous iterations.  
   */
    async getStructure(builderId, templateId, allContentLessons: boolean = true) : Promise<Structure> {
      const allLessonsMap = new Map<number, BuilderLesson>();
      let fixedSequenceAllLessons: number = 0;
      
      let data = await Vue.prototype.$BuilderCache.getFile(`snaptshot_${builderId}`, FILESCACHE_KEYS.BUILDER_DATA);
      if(data && data.file){
        let structure = data.file;
        if(allContentLessons){      //Rebuild the allLessonsMap with the snapshot data
          structure.allContentLessons.forEach((lesson: BuilderLesson) => {
            lesson.fixedSequence = ++fixedSequenceAllLessons;
            allLessonsMap.set(lesson.id, lesson);
          });
          structure.allFillerLessons.forEach((lesson: BuilderLesson) => {
            lesson.fixedSequence = ++fixedSequenceAllLessons;
            allLessonsMap.set(lesson.id, lesson);
          });
        }
        return new Structure(structure.contents, 
            structure.fillers, 
            structure.funs, 
            structure.additionals, 
            structure.lessons, 
            structure.materials, 
            structure.projects, 
            structure.deckLibrary,
            structure.allContentLessons,
            structure.allFillerLessons,
            structure.allFunLessons,
            [],
            allLessonsMap
          );

      } else {
        let data = await Vue.prototype.$BuilderCache.getFile(`template_${templateId}`, FILESCACHE_KEYS.BUILDER_TEMPLATES);
        if(data && data.file) {
          //Extract all folders and lessons from the template structure (only the first template is used)
          const template = data.file.templates[0]
          const allContentLessons = [];
          const allFillerLessons = [];
          const allFunLessons = [];
          
          //Rebuild the allLesson by type and allLessonsMap with the template data
          await this.extractLessonFromSectionsData(template, FIXED.CONTENT, allContentLessons, allLessonsMap, fixedSequenceAllLessons);
          await this.extractLessonFromSectionsData(template, FIXED.FILLER, allFillerLessons, allLessonsMap, fixedSequenceAllLessons);
          await this.extractLessonFromSectionsData(template, FIXED.FUN, allFunLessons, null);
          
          return new Structure(data.file.contentStructure, 
            data.file.fillerStructure, 
            data.file.funStructure, 
            data.file.additionalStructure, 
            data.file.lessons, 
            template.materials, 
            template.projects, 
            template.deckLibrary,
            allContentLessons,
            allFillerLessons,
            allFunLessons,
            [],
            allLessonsMap
          );
        }
      }
      return undefined;
    }

  /**
   * This function is responsible for building the Section Tree according to the type of automatic folder selected by the user. 
   * Lessons taken from Filler and Fun retain the folder structure they had in the original sections.
   * @param allLessons
   * @param automaticFolderType 
   * @param templateData 
   */
  async buildSubjectStructure(builderData : BuilderData) : Promise<any>{
    const structure = await this.getStructure(builderData.builderId, builderData.templateId, false);
    const sorteLessons = Array.from(builderData.allLessonsMap.values()).sort((lesson1, lesson2) => lesson1.startDay - lesson2.startDay);
    const lastContentLessonIndex = findLastContentLesson();
    let lastParentId = null;
    let lastWeek = null;
    let lastMonth = null;
    let weekNameSequence = 0;
    let sectionSequence = -1;  //Use negative ids to identify structure folders
    let lessonSequence = 1

    for (let lessonIndex = 0; lessonIndex < sorteLessons.length; lessonIndex++) {
      const bl = sorteLessons[lessonIndex];
      const lesson = structure.lessons.get(bl.id);
      lesson.fixedSequence = lessonSequence++;
      lesson.startDay = bl.startDay;
      lesson.endDay = bl.endDay;
      lesson._fixedDate = bl.status === BUILDER_LESSON_STATUS.FIXED ? true : false;
      let parent = this.getParent(bl, structure, sorteLessons, lessonIndex, lastContentLessonIndex, lastParentId)

      switch (builderData.automaticFolderType) {
        case AUTOMATIC_FOLDER_BUILDER.NO_AUTOMATIC:
          parent._sections.push(lesson);
          break;
        case AUTOMATIC_FOLDER_BUILDER.AUTOMATIC_WEEK:
          for (let index = 0; index < bl.parent.weeks.length; index++) {
            let weekId = bl.parent.weeks[index]
            if(weekId === lastWeek?.weekId && parent.id === lastParentId){
              lastWeek._sections.push(checkLesson(index, lesson));
            } else {
              lastWeek = newFolder(sectionSequence--, parent.id, weekName(weekId), weekId, null, checkLesson(index, lesson));
              parent._sections.push(lastWeek);
            }
          }
          break;
        case AUTOMATIC_FOLDER_BUILDER.AUTOMATIC_MONTH:
          for (let index = 0; index < bl.parent.weeks.length; index++) {
            let weekId = bl.parent.weeks[index]
            let monthId = bl.parent.months[index]
            if(weekId === lastWeek?.weekId && parent.id === lastParentId){
              lastWeek._sections.push(checkLesson(index, lesson));
            } else {
              if(monthId !== lastMonth?.monthId || parent.id !== lastParentId){
                lastMonth = newFolder(sectionSequence--, parent.id, `${monthId.split('-')[0]}`, null, monthId, null);
                parent._sections.push(lastMonth);
              }
              lastWeek = newFolder(sectionSequence--, lastMonth.id, weekName(weekId), weekId, null, checkLesson(index, lesson));
              lastMonth._sections.push(lastWeek);
            }
          }
          break;
      }
      lastParentId = parent.id;
      structure.lessons.delete(bl.id);
    }

    // Populate additional lessons
    let additionalSequence = 1
    builderData.additionalLessons.forEach(bl => { 
      const lesson = structure.lessons.get(bl.id)
      lesson.fixedSequence = additionalSequence++;
      let structureSrc = bl.rootType === FIXED.FILLER ? structure.fillers : bl.rootType === FIXED.FUN ? structure.funs : structure.contents;  //TODO: Only contents in additiona, do remove?
      let parent = this.replicateStructure(structureSrc.get(bl.parent.parentId), structureSrc, structure.additionals, structure.additionalRoot)
      parent._sections.push(lesson);
      structure.lessons.delete(bl.id);
    });

    const funRoot = Array.from(structure.funs.values()).find(section => section.fixedSection === FIXED.FUN);
    const fillerRoot = Array.from(structure.fillers.values()).find(section => section.fixedSection === FIXED.FILLER);
    const contentRoot = Array.from(structure.contents.values()).find(section => section.fixedSection === FIXED.CONTENT);

    // Populate non-taken FUN/FILLER lessons, preserving their original structure on structure.funs /  structure.fillers
    
    let funSequence = 1, fillerSequence = 1;
    structure.lessons.forEach((lesson) => {
      let structureSrc;
      if (lesson.rootType === FIXED.FUN) {
        structureSrc = structure.funs;
        lesson.fixedSequence = funSequence++;
      } else {
        structureSrc = structure.fillers;
        lesson.fixedSequence = fillerSequence++;
      }
      // For Fun and Fillers, we will preserve the original structure, so we locate the parent using the initialParentId
      let parent = lesson.initialParentId ? structureSrc.get(lesson.initialParentId) : structureSrc.get(lesson.parentId);
      lesson.parentId = parent.id;  //Reassign the parentId in case the lesson was moved by the user
      parent.sections.push(lesson);
    });

    this.setTreeData(contentRoot);
    this.setTreeData(structure.additionalRoot);

    return {content: contentRoot,
      filler: fillerRoot,
      fun : funRoot,
      additional: structure.additionalRoot
    };

    /**
     * Builds a new folder structrure
     */
    function newFolder(id, parentId, name, weekId, monthId, children: any, fixedSection : string = null){
      return {id, 
        parentId, 
        name, 
        type: SECTION_TYPES.SECTION.code,
        fixedSection,
        _sections : children ? [children] : [], 
        weekId, monthId
      }
    }

    /**
     * Check if a lesson should be divided into Links according to the week in which it will be placed.
     */
    function checkLesson(index: number, lesson: any) {
      if(index > 0) 
        return { id: `${sectionSequence--}`, 
          type: SECTION_TYPES.SECTION_LINK.code, 
          name: `${lesson.name}`, 
          text: `{"target" : ${lesson.id}}` ,
          sections: []
        }
      return lesson;
    }

    /**
     * Build the folder name corresponding to a week.
     * When the Lesson has the same weekId as the previous Lesson, the name is retained. Otherwise, the week name counter is incremented.
     */   
    function weekName(weekId: string) {
      return weekId === lastWeek?.weekId ? `Week ${weekNameSequence}` : `Week ${++weekNameSequence}`;
    }
    
    /**
     * Finds the last Lesson with rootType = 'CONTENT'
     * @returns 
     */
    function findLastContentLesson(){
      for (let i = sorteLessons.length - 1; i >= 0; i--) {
        if (sorteLessons[i].rootType === FIXED.CONTENT && sorteLessons[i].movedByBuilder === false)
          return i;
      }
      return 0;
    }
  }

      //TODO: Delete
    private printFolder(folder: any, indent: string = '') { 
      // console.log(`${indent}parentId: ${folder.parentId}, id: ${folder.id}, name: ${folder.name}, order: ${folder.order}`);
      // console.log(`${indent}${folder.name} id: ${folder.id}  type: ${folder.type} fixedSequence: ${folder.fixedSequence}, _sections ${folder._sections?.length}`);
      if(folder.fixedSection === FIXED.LESSON)
        return;
      if (folder.sections) 
        folder.sections.forEach(subFolder =>  this.printFolder(subFolder, indent + '|    '));
    }
  
  /**
   * Gets the parent section according the Lesson Root Type and the Lesson position
   */
  private getParent(lesson : BuilderLesson, 
                    structure : Structure, 
                    lessons: BuilderLesson[],
                    lessonIndex: number,
                    lastContentLessonIndex: number,
                    lastParentId: any) : any{
    const lastParent = lastParentId ? structure.contents.get(lastParentId) : null;
    
    //For Fun or Filler Lessons get the parent according the Lesson Root Type
    if (lesson.rootType === FIXED.FILLER || lesson.rootType === FIXED.FUN) { 
      const lessonParent = lesson.rootType === FIXED.FILLER ? 
                            structure.fillers.get(lesson.parent.parentId) : 
                            structure.funs.get(lesson.parent.parentId);

      if(!lastParent)
        return structure.contentRoot;

      if(lastParent.id === lessonParent.id)
        return lastParent;

      if(lessonIndex <= lastContentLessonIndex){       // Process the Fun/Filler Lessons that are located among the Content Lessons  
        return findParentByPosition(FIXED.CONTENT, this);        
      }else{                                           //  Process the Fun/Filler Lessons that are located after the Content Lessons:
        //If the Lesson is from Fillers, just replicate the structure at the end
        if(lesson.rootType === FIXED.FILLER)   
          return this.replicateStructure(lessonParent, structure.fillers, structure.contents, structure.contentRoot);

        // For Fun Lessons, a common parent is searched between the last and the next Filler. If none exists, the root is returned.
        return findParentByPosition(FIXED.FILLER, this);
      }      
     } else {
      const lessonParent = structure.contents.get(lesson.parent.parentId);
      if(lastParent?.id === lessonParent.id)
        return lastParent;

      //If the lesson was moved manually or by a rule, find the parent according the lesson position
      if(lesson.movedByBuilder === true){  
        return findParentByPosition(FIXED.CONTENT, this);        
      }else{
        this.checkParentStructure(lessonParent, structure.contents)
        return lessonParent;
      }
    }

     /** 
      * Finds the parent where a Lesson should be positioned according to its relative location to other Lessons. 
      * If the Lesson is followed by other Lessons of the target rootType, the common parent is searched between 
      * the previous and next Lessons. If no common parent exists or if the Lesson is not followed by relevant Lessons, 
      * the root node is returned.
      * 
      * When rootType is CONTENT:
      * - The search for the nextLesson is performed among the 'lessons' located after the current Lesson, considering only those not moved manually or by rules.
      * - If a nextLesson is found, its parent will be located in contentStructure.
      * 
      * When rootType is FILLER:
      * - The search for the nextLesson is performed among 'lessons' of the same type, located after the current Lesson.
      * - If a nextLesson is found, its parent will be located in fillerStructure.
     */
    function findParentByPosition(rootType: string = FIXED.CONTENT, context: any) {
        const nextLesson =  findNextLesson(rootType);
        if (nextLesson) {
          const nextParent = rootType === FIXED.CONTENT ? structure.contents.get(nextLesson.parent.parentId) : structure.fillers.get(nextLesson.parent.parentId);
          if (lastParent?.id === nextParent?.id) 
            return lastParent;
          //Check if the Content Lesson was previously located in the next parent
          if(rootType === FIXED.CONTENT && lesson.parent.parentId === nextParent?.id){
            context.checkParentStructure(nextParent, structure.contents)
            return nextParent;   
          }

          const commonId = findCommonBranch(lastParent, nextParent);
          if (commonId && structure.contents.has(commonId)) 
            return structure.contents.get(commonId);
        }
        return structure.contentRoot;
      }
      /**
       * Finds the next Lesson by rootType:
       * When CONTENT, finds for a Content Lesson after current Lesson that has not been moved manually or by a rule 
       * When FILLER, finds for a Filler Lesson after current Lessson
       */
      function findNextLesson(rootType: string = FIXED.CONTENT): BuilderLesson | null {
        if(rootType === FIXED.CONTENT){
          for (let index = lessonIndex + 1; index <= lastContentLessonIndex; index++) {
            if (lessons[index].rootType === FIXED.CONTENT && !lessons[index].movedByBuilder)
              return lessons[index];  
          }
        } else if(rootType === FIXED.FILLER){
          for (let index = lessonIndex + 1; index < lessons.length; index++) {
            if (lessons[index].rootType === FIXED.FILLER)
              return lessons[index];
          }
        }
        return null;
      }
      /**
       * Find the common ancestor between two parent nodes (the deepest one in the branches).
       */
      function findCommonBranch(parent: any, nextParent: any): any {
        if (!parent || !nextParent) return null;
        for (let i = parent.path.length - 1; i >= 0; i--) {
          if (nextParent.path.includes(parent.path[i])) 
            return parent.path[i];
        }
        return null;
      }
  }

  /**
   * This function is responsible for checking if a folder and its entire hierarchy are in the section tree. 
   * If not present, it creates the folder hierarchy from the root branch.
   * @param folder 
   * @param contentFolders 
   */
  private checkParentStructure(folder: any, contentFolders:  Map<number, any>): void {
    if(folder.parentId){
      let parent = contentFolders.get(folder.parentId);
      if(!parent._sections.some(section => section.id === folder.id))
        parent._sections.push(folder)
      this.checkParentStructure(parent, contentFolders);
    }
  }

  /**
   * This function is responsible for replicating the folder hierarchy for lessons taken from the Filler or Fun sections.
   * For the replicated folders, the 'sectionId = -sourceFolderId' is used so that all the lessons moved to the Content section can be properly located.
   * @param folderId 
   * @param srcFolders 
   * @param targetFolders 
   * @returns 
   */
  private replicateStructure(parent: any, srcFolders:  Map<number, any>, targetFolders:  Map<number, any>, root : any): any | undefined{
    let id = _id(parent.id);
    if(targetFolders.has(id)){
      return targetFolders.get(id);
    }

    const tree = [];
    let folder = parent;
    while(folder.parentId){   //Get the section tree excluding the root
      const copy = {...folder, id: _id(folder.id), parentId: _id(folder.parentId), sections: [], _sections: []};
      tree.push(copy)
      folder = srcFolders.get(folder.parentId);
    }
    
    if(tree.length > 0){ 
      folder = tree.pop() //Set the first node
      if(!targetFolders.has(folder.id)){
        folder.parentId = root.id;
        root._sections.push(folder)
        targetFolders.set(folder.id, folder);
      }
      //Iterate the next tree nodes, each parent should be present from the previous iteration
      while(tree.length > 0){
        folder = tree.pop()
        if(!targetFolders.has(folder.id)){
          targetFolders.get(folder.parentId)._sections.push(folder);
          targetFolders.set(folder.id, folder)
        }
      }
    }
    return targetFolders.get(id) || root;
    
    /**
     * Generates an ID by composing the root ID and the section ID to replicate.
     * The negative value allows identifying that this will be a structure folder.
     */ 
    function _id(id: number): number {
      return -parseInt(`${root.id}${id}`);
    }
  }

  /**
   * Populates the Section.sections with the calculated subsection tree (Section._sections)
   * Set the order according the tree
   * Set the parentId  according the tree
   * Check for empty folders (no lessons after building) on original tree to add on the resulting tree
   * @param folder 
   */
  private setTreeData(folder: any): void {
    if(folder.fixedSection === FIXED.LESSON)  //Stop recursion on Lessons
      return;
    
    let iniSections = folder.sections || [];
    let newSections = folder._sections || [];
    
    /* Find missing elements (empty folders) and add them to the final results.
      - If the missing element is the first one, add to the beginning
      - Else, find the previous element or the next and try to locate near to it
    */
    iniSections.forEach((child, idx) => {
      const exists = newSections.some(section => section.id === child.id);
      if (!exists) {
        if (idx === 0) {                   
          newSections.unshift(child);   // Add to the beginning
        } else {
          const prev = iniSections[idx - 1];
          const prevIdx = newSections.findIndex(section => section.id === prev.id);
          if (prevIdx !== -1) {             // If the previous element is in newSections, insert it after it
            newSections.splice(prevIdx + 1, 0, child);
          } else {                           // If the previous is not, find the next
            const next = iniSections[idx + 1];
            const nextIdx = next ? newSections.findIndex(section => section.id === next.id) : -1;
            if (nextIdx !== -1) {           // If next is in newSections, insert missing before it
              newSections.splice(nextIdx, 0, child);
            } else {                        // If not present, locate at the end
              newSections.push(child);
            }
          }
        }
      }
    });
  
    
    let order = 1;
    newSections.forEach(sub =>  {
      sub.parentId = folder.id
      sub.order = order++;
      this.setTreeData(sub);         
    });
    
    folder.sections = newSections;
    delete folder._sections;      
    
  }


  private extractSections(sections: any[], allLessonsByRoot: BuilderLesson[], rootFixedSection: string, suggestedLessonDuration: number, allLessonsMap: Map<number, BuilderLesson>, fixedSequenceAllLessons: number = 0): number {
    sections.forEach((section) => {
        if (section.fixedSection === FIXED.LESSON) {
          section.lessonDuration = section.lessonDuration??suggestedLessonDuration;
          let lesson = sectionToBuilderLesson(section, rootFixedSection);
          if (section.sections && section.sections.length > 0) {
              let components: Set<number> = new Set();
              let tagsSection: Set<string> = new Set();
              this.extractComponentByObject(section.sections, components);
              this.extractLabelByObject(section.sections, tagsSection);
              lesson.objectsComponent =  Array.from(components);
              lesson.tagsSection =  Array.from(tagsSection);
          }
          if(allLessonsMap) { 
            lesson.fixedSequence = ++fixedSequenceAllLessons;
            allLessonsMap.set(lesson.id, lesson);
          }
          allLessonsByRoot.push(lesson);
        } else if (section.sections && section.sections.length > 0) {
          fixedSequenceAllLessons = this.extractSections(section.sections, allLessonsByRoot, rootFixedSection, suggestedLessonDuration, allLessonsMap, fixedSequenceAllLessons);
        }
    });

    return fixedSequenceAllLessons
  }

  private extractLessonFromDownloadData(sections: any[], builderData:  BuilderData, fixedLessons: Map<number, number>) {
    sections.forEach((section) => {
      if (section.fixedSection === FIXED.LESSON) {
        let lesson = sectionToBuilderLesson(section, section.initialRootType);
        if (section.sections && section.sections.length > 0) {
            let components: Set<number> = new Set();
            let tagsSection: Set<string> = new Set();
            this.extractComponentByObject(section.sections, components);
            this.extractLabelByObject(section.sections, tagsSection);
            lesson.objectsComponent =  Array.from(components);
            lesson.tagsSection =  Array.from(tagsSection);
        }

        if(section.submissionDates //if lesson has start date on server
          && (builderData.subject.status === BUILDER_STATUS.DONE  //if subject is READY then we need to assign dates
            || (builderData.subject.status === BUILDER_STATUS.DRAFT // or if the subject is DRAFT and this lesson is into fixedlessons and the startDate is on the future then we need to assing dates data
              && fixedLessons.has(lesson.id) 
              && this.validateActiveLessonSchoolDate(section.submissionDates.startDate)))) {
          lesson.startDay = section.submissionDates.startSchoolDay;
          lesson.endDay = section.submissionDates.endSchoolDay;
          lesson.startSchoolDate = section.submissionDates.startDate;
          lesson.endSchoolDate = section.submissionDates.endDate;
          lesson.status = (fixedLessons.has(lesson.id)) ? BUILDER_LESSON_STATUS.FIXED : BUILDER_LESSON_STATUS.ASSIGNED;
        }
        builderData.allLessonsMap.set(lesson.id, lesson);

        if(section.initialRootType === FIXED.CONTENT) { 
          builderData.allContentLessons.push(lesson);
        } else if(section.initialRootType === FIXED.FUN) { 
          builderData.allFunLessons.push(lesson);
        } else if(section.initialRootType === FIXED.FILLER) { 
          builderData.allFillerLessons.push(lesson);
        } else if(section.initialRootType === FIXED.ADDITIONAL) { 
          builderData.additionalLessons.push(lesson);
        }
      } else if (section.sections && section.sections.length > 0) {
        this.extractLessonFromDownloadData(section.sections, builderData, fixedLessons);
      }
    });
  }

  private validateActiveLessonSchoolDate(startLessonDate) {
    if(!startLessonDate) {
      return false;
    }
    const today = new Date();
    const startSchoolDate = new Date(startLessonDate);
    if(startSchoolDate.getTime() > today.getTime()) {
      return true;
    }
    return false;
  }

  private extractComponentByObject(sections: any[], components: Set<number>) {
      sections.forEach((section) => {
          if (section.component && section.component.id !== undefined) {
              components.add(section.component.id);
          } else if (section.sections && section.sections.length > 0) {
              this.extractComponentByObject(section.sections, components);
          }
      });
  }

  private extractLabelByObject(sections: any[], tagsSection: Set<string>) {
    sections.forEach((section) => {
        if (section.labelSections) {
            tagsSection.add(section.labelSections);
        } else if (section.sections && section.sections.length > 0) {
            this.extractLabelByObject(section.sections, tagsSection);
        }
    });
  }

  async reorderRulesApplied(rule, rulesApplied: BuilderRule[]) {
    if (
      rule.type === BUILDER_RULES.START_LESSON || 
      rule.type === BUILDER_RULES.PLACE_LABELS_ON_WORKING_DAY
    ) {
      // put active false each rule with same type
      rulesApplied
        .filter(r => r.type === rule.type && (!rule.label || rule.label === r.label))
        .forEach(r => r.active = false);
  
      rule.active = true;
      rule.time = new Date().getTime();
      rulesApplied.push(rule);
  
      const rulesAppliedOrdered: BuilderRule[] = [];
  
      // group rules by type
      const listGroupedByRuleType = rulesApplied.reduce((acc, r) => {
        if (!acc[r.type]) {
          acc[r.type] = [];
        }
        acc[r.type].push(r);
        return acc;
      }, {} as Record<string, BuilderRule[]>);
  
      // process each group
      Object.entries(listGroupedByRuleType).forEach(([type, rules]) => {
        if (type === BUILDER_RULES.START_LESSON) {
          // reorder list desc
          const listByStartLesson = rules.sort((f1, f2) => (f2.time ?? 0) - (f1.time ?? 0));
          rulesAppliedOrdered.push(...listByStartLesson);
        } else {
          // group list by label and reorder desc
          const listByTag = Object.values(
            rules.reduce((acc, r) => {
              const key = r.label ?? '';
              if (!acc[key]) {
                acc[key] = [];
              }
              acc[key].push(r);
              return acc;
            }, {} as Record<string, BuilderRule[]>)
          ).flatMap(group => group.sort((f1, f2) => (f2.time ?? 0) - (f1.time ?? 0)));
          
          rulesAppliedOrdered.push(...listByTag);
        }
      });
  
      return rulesAppliedOrdered;
    }

    return rulesApplied;
  }

  removeElementsFromArrayNumber(numbers: number[], from: number): number[] {
    const index = numbers.indexOf(from);
    if (index === -1) {
        return numbers; 
    }
    return numbers.slice(0, index);
  }

  getPendingTimeFromLessons(lessons: number[], allLesson: Map<number, BuilderLesson>): number {
    let pendingTime = 0;

    lessons.forEach((id) => {
        const lessonFound = allLesson.get(id);
        if (lessonFound) {
          pendingTime += lessonFound.duration ? lessonFound.duration : 0; 
        }
    });

    return pendingTime;
}

  async getBuilderDataToSaveOnStore(contentDetail: any, automaticStructure: any, structure: any) {
    if(contentDetail) {
      let content = {...contentDetail};
      content.contentSections = automaticStructure.content;
      content.sections = automaticStructure.content.sections;
      content.fillerSections = automaticStructure.filler;
      content.funSections = automaticStructure.fun;
      content.additionalSections = automaticStructure.additional;
      content.materials = {...structure.materials};
      content.projects = [...structure.projects];
      content.deckLibrary = {...structure.deckLibrary};
      return content;
    }
    return null;
  }

  async getLessonStructureById(lessonId : number, builderId, templateId) {
    const data = await this.getStructure(builderId, templateId);

    if(data && data.lessons && data.lessons.has(lessonId)) {
      return data.lessons.get(lessonId);
    }
    return null;
  }

  async deleteAllDataFromIndexDB(builderId) {
    await Vue.prototype.$BuilderCache.removeFile(`builder_${builderId}`, FILESCACHE_KEYS.BUILDER_DATA);
    await Vue.prototype.$BuilderCache.removeFile(`snaptshot_${builderId}`, FILESCACHE_KEYS.BUILDER_DATA);
    await Vue.prototype.$BuilderCache.removeFile(`sync_request_${builderId}`, FILESCACHE_KEYS.BUILDER_QUEUE);
    await Vue.prototype.$BuilderCache.removeFile(`sync_response_${builderId}`, FILESCACHE_KEYS.BUILDER_QUEUE);
  }
}

export const BuilderService = new BuilderServiceClass();